import React, { useState, useEffect, useMemo } from 'react';
import { convertTime } from './utils';
import { Box, Autocomplete, TextField, Checkbox, ListItemText, Stack, MenuItem } from '@mui/material';
import { TagsChip, TrendingChip } from '../components/chip';
import { formatDateInTimeZone, getTimeZoneIana } from './dateTimeUtils';
import { TZDate } from '@date-fns/tz';
import { formatDistanceToNow } from 'date-fns';
import { DateRangeEntry } from '../components/dialogEntries/DateRangeEntry';
import { DateTimeRangeEntry } from '../components/dialogEntries/DateTimeRangeEntry';
import { useGetTags } from '../portalQueryApi/queryTagsApi';

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const TBL_MAX_UI_PAGINATION = 1000;
export const PAGINATION_DEF = {
  pageSize: TBL_MAX_UI_PAGINATION,
  pageIndex: 0,
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const filterFnDateEq = (row, id, filterValue, tzInfo) => {
  const timezoneIanaToUse = tzInfo?.iana ?? getTimeZoneIana();
  const rowDate = row?.getValue(id);
  const zonedRowDate = new TZDate(rowDate, timezoneIanaToUse);
  const filteredDate = new Date(filterValue);
  return zonedRowDate?.toDateString() === filteredDate.toDateString();
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const filterFnDateRange = (row, id, filterValue, tzInfo) => {
  const timezoneIanaToUse = tzInfo?.iana ?? getTimeZoneIana();
  const rowDate = row?.getValue(id);
  const zonedRowDate = new TZDate(rowDate, timezoneIanaToUse);
  const [start, end] = filterValue || [];
  if (start && zonedRowDate < start) return false;
  if (end && zonedRowDate > end) return false;
  return true;
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const filterDateTimeRange = ({ column }) => {
  const filterValue = column?.getFilterValue() || [null, null];

  return (
    <DateTimeRangeEntry
      value={{ from: filterValue[0], to: filterValue[1] }}
      onChange={(newValue) => {
        column.setFilterValue(newValue ? [newValue?.from, newValue?.to] : [null, null]);
      }}
      showDates={true}
      compact={true}
      showCalendarIcon={false}
      sx={{
        width: '100%',
        borderColor: 'transparent',
        color: (theme) => theme.palette.text.primary,
        opacity: 0.55,
        py: 0,
        borderBottom: '1px solid',
        borderBottomColor: (theme) => theme.palette.text.primary,
        borderRadius: 0,
        height: '28px !important',
        '& .MuiTypography-root': {
          fontSize: '13px',
        },
      }}
    />
  );
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const filterDateRange = (props = {}) => {
  const { table, column } = props;
  const filterValue = column?.getFilterValue() || [null, null];
  const tzInfo = table?.options?.meta?.tzInfo;
  const timezoneIana = tzInfo?.iana ?? getTimeZoneIana();

  return (
    <DateRangeEntry
      value={{ from: filterValue[0], to: filterValue[1] }}
      onChange={(newValue) => {
        column.setFilterValue(newValue ? [newValue?.from, newValue?.to] : [null, null]);
      }}
      showDates={true}
      compact={true}
      showCalendarIcon={false}
      timezoneIana={timezoneIana}
      sx={{
        width: '100%',
        borderColor: 'transparent',
        color: (theme) => theme.palette.text.primary,
        opacity: 0.55,
        py: 0,
        borderBottom: '1px solid',
        borderBottomColor: (theme) => theme.palette.text.primary,
        borderRadius: 0,
        height: '28px !important',
        '& .MuiTypography-root': {
          fontSize: '13px',
        },
      }}
    />
  );
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const filterNumberRange = ({ column }) => {
  const { setFilterValue } = column;
  const [operator, setOperator] = useState('>');
  const [value, setValue] = useState('');

  const headerLabel = column?.columnDef?.header || 'Enter number';

  useEffect(() => {
    if (!value) {
      setFilterValue(undefined);
    } else {
      setFilterValue({ operator, value: parseFloat(value) });
    }
  }, [operator, value]);

  return (
    <Stack direction='row' spacing={1} sx={{ '& .MuiInput-root': { fontSize: '13px' } }}>
      <TextField variant='standard' select value={operator} onChange={(e) => setOperator(e.target.value)}>
        <MenuItem value='>'>{'>'}</MenuItem>
        <MenuItem value='>='>{'>='}</MenuItem>
        <MenuItem value='='>{'='}</MenuItem>
        <MenuItem value='<='>{'<='}</MenuItem>
        <MenuItem value='<'>{'<'}</MenuItem>
      </TextField>
      <TextField variant='standard' type='number' value={value} onChange={(e) => setValue(e.target.value)} placeholder={headerLabel} />
    </Stack>
  );
};


const UNTAGGED_LABEL = 'Untagged';
const UNTAGGED_ID = '----untagged----';
const isUntagged = (o) => o?.entityId === UNTAGGED_ID || (typeof o?.tagValue === 'string' && o.tagValue.trim().toLowerCase() === 'untagged');

// ensure "Untagged" shows first if present (or if you synthesize it)
const orderUntaggedFirst = (arr) => {
  const untagged = [];
  const rest = [];
  arr.forEach((o) => (isUntagged(o) ? untagged.push(o) : rest.push(o)));
  untagged.sort((a, b) => a.tagValue.localeCompare(b.tagValue));
  rest.sort((a, b) => a.tagValue.localeCompare(b.tagValue));
  return [...untagged, ...rest];
};

// helper: per-column format switch ('value' for UI, 'id' for backend)
const getTagFilterFormat = (column) => (column?.columnDef?.meta?.tagFilterFormat === 'id' ? 'id' : 'value');

// Return full objects instead not just string values
const useTagsOptionsFromApi = () => {
  const { data: allTags, status } = useGetTags({ entityType: 'Tags', filterByUserTags: true });

  const options = useMemo(() => {
    if (status !== 'success' || !allTags) return [];
    // Build unique list by tagValue while preserving entityId
    const seen = new Set();
    const items = [];
    for (const t of allTags) {
      const tagValue = t?.tagValue;
      if (!tagValue || seen.has(tagValue)) continue;
      seen.add(tagValue);
      items.push({ tagValue, entityId: t?.entityId });
    }
    return items;
  }, [allTags, status]);

  return options;
};

export const filterTags = ({ column }) => {
  const format = getTagFilterFormat(column); // 'value' | 'id'
  const apiOptions = useTagsOptionsFromApi(); // get options from API

  const options = useMemo(() => orderUntaggedFirst(apiOptions), [apiOptions]);

  const filterVals = column.getFilterValue() ?? [];
  const selectedSet = useMemo(() => new Set(filterVals.map(String)), [filterVals]);

  const valueObjects = useMemo(() => {
    if (format === 'id') {
      // selected when the option's entityId is in the filter (including UNTAGGED_ID)
      return options.filter((o) => selectedSet.has(String(o.entityId)));
    }
    // 'value' mode
    return options.filter((o) => selectedSet.has(String(o.tagValue)));
  }, [options, selectedSet, format]);

  const hasSelection = valueObjects.length > 0;
  const [focused, setFocused] = useState(false);

  return (
    <Autocomplete
      multiple
      size='small'
      options={options}
      value={valueObjects}
      getOptionLabel={(o) => o?.tagValue ?? ''}
      isOptionEqualToValue={(o, v) => (o?.entityId && v?.entityId ? o.entityId === v.entityId : o?.tagValue === v?.tagValue)}
      onChange={(_e, newObjs) => {
        if (format === 'id') {
          // ✅ store IDs (and include the sentinel ID if Untagged selected)
          const ids = newObjs.map((o) => o.entityId);
          column.setFilterValue(ids);
        } else {
          // value mode (UI-only filtering): store tagValue strings
          column.setFilterValue(newObjs.map((o) => o.tagValue));
        }
      }}
      filterSelectedOptions={false}
      disableCloseOnSelect
      popupIcon={null}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      renderOption={(props, option, { selected }) => (
        <Box
          component='li'
          {...props}
          sx={{
            ...(isUntagged(option) && {
              borderBottom: '2px solid',
              borderColor: 'divider',
            }),
          }}
        >
          <Checkbox checked={selected} tabIndex={-1} disableRipple size='small' sx={{ mr: 1, p: 0.25 }} />
          <ListItemText primary={option.tagValue} />
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={hasSelection || focused ? '' : 'Filter tags…'}
          variant='standard'
          InputProps={{ ...params.InputProps, style: { fontSize: '13px', height: '27.69px', padding: '0 6px' } }}
          inputProps={{ ...params.inputProps, style: { fontSize: '13px' } }}
        />
      )}
      sx={{
        '& .MuiFormControl-root': { marginTop: 0, marginBottom: 0 },
        '& .MuiAutocomplete-inputRoot': { padding: '0 !important', minHeight: '27.69px' },
        '& .MuiInputBase-input': { fontSize: '13px', padding: '2px 4px !important' },
        '& .MuiChip-root': { height: '20px', fontSize: '12px' },
      }}
    />
  );
};

export const filterFnTags = (row, id, filterValue) => {
  // normalize filter tokens
  const filters = Array.isArray(filterValue)
    ? filterValue.filter((v) => v != null && String(v).trim() !== '').map(String)
    : filterValue != null
    ? [String(filterValue)]
    : [];

  if (filters.length === 0) return true;

  const rowTags = row.getValue(id) ?? [];
  if (!Array.isArray(rowTags)) return false;

  // build lookup sets once
  const rowTagValues = rowTags
    .map((t) => (t && typeof t === 'object' ? t.tagValue : t))
    .filter((v) => v != null && String(v).trim() !== '')
    .map(String);
  const rowTagIds = rowTags
    .map((t) => (t && typeof t === 'object' ? t.entityId : null))
    .filter((v) => v != null)
    .map(String);

  const valueSet = new Set(rowTagValues);
  const idSet = new Set(rowTagIds);

  // “Untagged” (by sentinel or label) means: row has no tags
  const hasUntaggedToken = filters.some((f) => f === UNTAGGED_ID || f.trim().toLowerCase() === UNTAGGED_LABEL.toLowerCase());
  const isRowUntagged = rowTagValues.length === 0;

  // non-untagged tokens can be either values or ids (ANY match)
  const named = filters.filter((f) => !(f === UNTAGGED_ID || f.trim().toLowerCase() === UNTAGGED_LABEL.toLowerCase()));

  const matchesNamed = named.length ? named.some((f) => valueSet.has(f) || idSet.has(f)) : false;

  return matchesNamed || (hasUntaggedToken && isRowUntagged);
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const getCellValue = (cell) => {
  if (!cell) return null;
  let cellValue;
  if (typeof cell === 'string' || typeof cell === 'number') {
    cellValue = cell;
  } else if (typeof cell === 'object') {
    if ('getValue' in cell && typeof cell.getValue === 'function') {
      cellValue = cell.getValue();
    } else {
      cellValue = cell;
    }
  } else {
    return null;
  }
  return cellValue;
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const cellFormatDeviceStatus = ({ cell }) => {
  const cellValue = getCellValue(cell);
  if (!cellValue) return null;
  return (
    <Box
      component='span'
      sx={(theme) => ({
        backgroundColor:
          cellValue === 'Not Connected' // offline
            ? theme.palette.gray150
            : cellValue === 'Standby'
            ? theme.palette.warning1
            : theme.palette.success80,
        borderRadius: '0.25rem',
        color: '#fff',
        width: '14ch',
        p: '0.25rem',
        textAlign: 'center',
      })}
    >
      {cellValue}
    </Box>
  );
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const cellFormatTrendingChip = ({ cell }) => {
  let cellValue = getCellValue(cell);
  if (!cellValue) return null;
  return <TrendingChip label={cellValue} />;
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const cellFormatOdometer = ({ cell }) => {
  let cellValue = getCellValue(cell);
  if (!cellValue) return '0 Miles';

  // Odometer is given in hectometre (1/10 of a kilometer)
  // So first we convert it to kilometer and then to miles
  cellValue = Math.round(Number(cellValue) * 0.1 * 0.621371);
  return `${cellValue.toLocaleString()} Miles`;
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const cellFormatPercentage = ({ cell }) => {
  const cellValue = getCellValue(cell);
  if (!cellValue) return '0%';
  return `${cellValue.toFixed(0)}%`;
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const cellFormatDistance = ({ cell }) => {
  const cellValue = getCellValue(cell);
  if (!cellValue) return '0 Miles';
  return `${Number(cellValue).toLocaleString()} Miles`;
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const cellFormatTimeDistanceFromNow = ({ cell }) => {
  const cellValue = getCellValue(cell);
  return isNaN(cellValue.getTime()) || !cellValue.getTime() ? 'Never' : formatDistanceToNow(cellValue, { includeSeconds: true, addSuffix: true });
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const cellFormatDateTime = ({ cell, table }) => {
  const cellValue = getCellValue(cell);
  if (!cellValue) return 'N/A';
  return isNaN(cellValue.getTime()) ? 'N/A' : formatDateInTimeZone(cellValue, table.options.meta.tzInfo, true);
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const accessorFnTags = (row) => {
  return row.Tags ? row.Tags.map((tag) => ({ tagValue: tag.tagValue, entityId: tag.entityId })) : [];
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const cellFormatTags = ({ cell }) => {
  const cellValue = getCellValue(cell);
  if (!Array.isArray(cellValue) || !cellValue.length) return null;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {cellValue.map((tag) => (
        <TagsChip key={tag.entityId} label={tag.tagValue} size={'small'} />
      ))}
    </Box>
  );
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
// Keep one format accross all tables!!!
export const converMillisecToMinutes = (row) => {
  const milliseconds = Math.max(0, new Date(row.endTime) - new Date(row.startTime));
  return convertTime(milliseconds);
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const cellFormatUSD = ({ cell }) => {
  const cellValue = getCellValue(cell);
  if (!cellValue) return '$0.0';
  return `${cellValue?.toLocaleString?.('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}`;
};
